"use client";

import type { ClerkAPIResponseError } from "@clerk/shared/error";
import type { useSignIn } from "@klarify/auth/client";
import { Button } from "@klarify/ui/components/ui/button";
import { cn } from "@klarify/ui/lib/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

interface TwoFactorVerificationProps {
	signIn: NonNullable<ReturnType<typeof useSignIn>["signIn"]>;
	setActive: ReturnType<typeof useSignIn>["setActive"];
	forceRedirectUrl?: string;
	redirectUrl?: string;
	onBack: () => void;
	onRedirectStart?: () => void;
}

export function TwoFactorVerification({
	signIn,
	setActive,
	forceRedirectUrl,
	redirectUrl,
	onBack,
	onRedirectStart,
}: TwoFactorVerificationProps) {
	const { t } = useLingui();
	const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [lastAttemptedCode, setLastAttemptedCode] = useState("");
	const [lastChangedIndex, setLastChangedIndex] = useState<number | null>(null);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Focus first input on mount
	useEffect(() => {
		setTimeout(() => {
			inputRefs.current[0]?.focus();
		}, 100);
	}, []);

	const handleDigitChange = (index: number, value: string) => {
		let newValue = value;
		if (value.length > 1) {
			newValue = value[value.length - 1] || "";
		}

		if (newValue && !/^\d$/.test(newValue)) {
			return;
		}

		const newDigits = [...digits];
		newDigits[index] = newValue;
		setDigits(newDigits);
		setLastChangedIndex(index);

		if (newValue && index < 5) {
			const nextInput = inputRefs.current[index + 1];
			setTimeout(() => {
				nextInput?.focus();
				nextInput?.select();
			}, 0);
		}
	};

	const handleKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if (e.key === "Backspace" && !digits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);

		if (!/^\d+$/.test(pastedData)) {
			return;
		}

		const newDigits = [...digits];
		for (let i = 0; i < pastedData.length && i < 6; i++) {
			newDigits[i] = pastedData[i] || "";
		}
		setDigits(newDigits);

		const nextIndex = Math.min(pastedData.length, 5);
		inputRefs.current[nextIndex]?.focus();
	};

	const verifyCode = useCallback(
		async (code: string) => {
			if (isSubmitting) {
				return;
			}

			setIsSubmitting(true);
			setError("");
			setLastAttemptedCode(code);

			try {
				const result = await signIn.attemptSecondFactor({
					strategy: "totp",
					code,
				});

				if (result.status === "complete") {
					onRedirectStart?.();
					if (setActive) {
						await setActive({ session: result.createdSessionId });
					}
					window.location.href = forceRedirectUrl ?? redirectUrl ?? "/";
				} else {
					setError(t`Verification incomplete. Please try again.`);
					setIsSubmitting(false);
				}
			} catch (err: unknown) {
				const clerkError = err as ClerkAPIResponseError;
				setError(
					clerkError.longMessage ??
						clerkError?.errors?.[0]?.longMessage ??
						t`Invalid verification code. Please try again.`,
				);
				setIsSubmitting(false);
			}
		},
		[
			signIn,
			isSubmitting,
			setActive,
			forceRedirectUrl,
			redirectUrl,
			onRedirectStart,
			t,
		],
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const code = digits.join("");
		if (code.length !== 6) {
			setError(t`Please enter all 6 digits`);
			return;
		}
		await verifyCode(code);
	};

	// Auto-submit when all digits are entered
	useEffect(() => {
		const code = digits.join("");
		if (
			code.length === 6 &&
			lastChangedIndex === 5 &&
			!isSubmitting &&
			code !== lastAttemptedCode
		) {
			void verifyCode(code);
		}
	}, [digits, lastChangedIndex, isSubmitting, lastAttemptedCode, verifyCode]);

	return (
		<div className="w-full max-w-md rounded-xl border bg-white p-7 shadow-custom lg:p-8">
			<div className="mb-6">
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={onBack}
					className="mb-2 -ml-2 gap-1 text-klarify-gray-mod-500 hover:text-klarify-gray-mod-700"
				>
					<ArrowLeft size={16} />
					<Trans>Back</Trans>
				</Button>
				<h1 className="font-semibold text-2xl text-klarify-gray-mod-900">
					<Trans>Two-step verification</Trans>
				</h1>
				<p className="mt-2 text-klarify-gray-mod-600 text-sm">
					<Trans>Enter the 6-digit code from your authenticator app</Trans>
				</p>
			</div>

			<form
				onSubmit={handleSubmit}
				className="space-y-4"
			>
				<div className="flex justify-center gap-2">
					{digits.map((digit, index) => (
						<input
							key={index}
							ref={(el) => {
								inputRefs.current[index] = el;
							}}
							type="text"
							inputMode="numeric"
							value={digit}
							onChange={(e) => handleDigitChange(index, e.target.value)}
							onKeyDown={(e) => handleKeyDown(index, e)}
							onFocus={() => inputRefs.current[index]?.select()}
							onPaste={handlePaste}
							disabled={isSubmitting}
							className={cn(
								"h-12 w-12 rounded-md border text-center font-semibold text-lg shadow-sm focus:outline-none focus:ring-1 disabled:bg-klarify-gray-mod-50",
								error
									? "border-destructive focus:border-destructive focus:ring-destructive"
									: "border-klarify-gray-mod-300 focus:border-primary focus:ring-primary",
							)}
							maxLength={1}
							required
							aria-label={t`Digit ${{ number: index + 1 }}`}
							aria-invalid={error ? "true" : "false"}
						/>
					))}
				</div>

				{error && (
					<div className="rounded-md bg-destructive/5 p-3 text-center text-destructive text-sm">
						{error}
					</div>
				)}

				<Button
					type="submit"
					disabled={isSubmitting}
					rounding="md"
					className="w-full"
				>
					{isSubmitting ? <Trans>Verifying...</Trans> : <Trans>Verify</Trans>}
				</Button>
			</form>
		</div>
	);
}
